import { api, ApiError } from '@/lib/api-client'
import { env } from '@/lib/env'
import { clearSession, getToken } from '@/lib/session'
import type {
  AdminDriver,
  DriverCreateRequest,
  DriverOption,
  MessageResponse,
  OtsDriverSyncResult,
  PageResponse,
  PeopleFilters,
  UploadResponse,
} from '@/types/api'

export function fetchDrivers(filters: PeopleFilters): Promise<PageResponse<AdminDriver>> {
  return api.get<PageResponse<AdminDriver>>('/api/admin/drivers', { query: { ...filters } })
}

export function fetchDriver(id: number): Promise<AdminDriver> {
  return api.get<AdminDriver>(`/api/admin/drivers/${id}`)
}

/**
 * Drivers an admin may assign: role DRIVER with an enabled account. Replaces
 * the legacy dashboard's hard-coded exclusion of driver id 19.
 */
export function fetchEligibleDrivers(): Promise<DriverOption[]> {
  return api.get<DriverOption[]>('/api/admin/drivers/eligible')
}

export function suspendDriver(id: number): Promise<MessageResponse> {
  return api.patch<MessageResponse>(`/api/admin/drivers/${id}/suspend`)
}

export function activateDriver(id: number): Promise<MessageResponse> {
  return api.patch<MessageResponse>(`/api/admin/drivers/${id}/activate`)
}

/**
 * Pulls the driver list from the OTS driver app now, instead of waiting for
 * the scheduled run: links drivers by phone number, creates accounts for new
 * OTS drivers, and suspends the ones OTS terminated.
 */
export function syncDriversFromOts(): Promise<OtsDriverSyncResult> {
  return api.post<OtsDriverSyncResult>('/api/admin/drivers/ots-sync')
}

export function createDriver(payload: DriverCreateRequest): Promise<MessageResponse> {
  return api.post<MessageResponse>('/api/admin/drivers/add', payload)
}

/** Stores the photo first; the returned URL is sent with the new driver. */
export function uploadDriverPhoto(file: File): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<UploadResponse>('/api/admin/drivers/photo', formData)
}

/**
 * Same upload, reported as it happens.
 *
 * `fetch` cannot report request progress, so this one call uses XMLHttpRequest:
 * a photo on a slow connection otherwise looks like the form has frozen.
 */
export function uploadDriverPhotoWithProgress(
  file: File,
  onProgress: (percent: number) => void,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)

    const request = new XMLHttpRequest()
    request.open('POST', `${env.apiBaseUrl}/api/admin/drivers/photo`)

    const token = getToken()
    if (token) request.setRequestHeader('Authorization', `Bearer ${token}`)
    request.setRequestHeader('Accept', 'application/json')

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }

    request.onload = () => {
      let payload: unknown = null
      try {
        payload = JSON.parse(request.responseText) as unknown
      } catch {
        payload = null
      }

      if (request.status >= 200 && request.status < 300) {
        const url = (payload as UploadResponse | null)?.url
        if (typeof url === 'string' && url.length > 0) {
          onProgress(100)
          resolve({ url })
          return
        }
        reject(new ApiError('The upload succeeded but returned no URL.', request.status, 'NO_URL'))
        return
      }

      if (request.status === 401) {
        clearSession()
      }

      const record = (payload ?? {}) as Record<string, unknown>
      const message =
        typeof record.error === 'string'
          ? record.error
          : typeof record.message === 'string'
            ? record.message
            : 'The photo could not be uploaded.'
      reject(new ApiError(message, request.status, `HTTP_${request.status}`))
    }

    request.onerror = () =>
      reject(new ApiError('Could not reach the API while uploading the photo.', 0, 'NETWORK'))
    request.ontimeout = () => reject(new ApiError('The photo upload timed out.', 0, 'TIMEOUT'))

    request.timeout = env.apiTimeoutMs
    request.send(formData)
  })
}
